import { CopyIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { UnsplashImage } from '../../../types/blogTypes';
import toastStore from '../../stores/toastStore';
import { apiClient } from '../../utils/apiClient';
import { useMobxInstance } from '../../utils/mobxUtil';

class ViewStore {
    searchText = '';
    searchedImages: UnsplashImage[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    setSearchText(value: string) {
        this.searchText = value;
    }

    async search() {
        const images = await apiClient.news.admin.searchUnsplashImage.$query({
            query: this.searchText,
        });

        runInAction(() => {
            this.searchedImages = images;
        });
    }

    async copyImage(imgElement: HTMLImageElement) {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = imgElement.naturalWidth;
            canvas.height = imgElement.naturalHeight;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Canvas context not available');
            }

            ctx.drawImage(imgElement, 0, 0);

            // iOS Safari 호환성: ClipboardItem을 사용자 제스처 컨텍스트 내에서 즉시 생성
            // blob은 Promise로 제공하면 나중에 resolve되어도 됨
            const blobPromise = new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to create blob'));
                    }
                }, 'image/png');
            });

            // 사용자 제스처 컨텍스트 내에서 즉시 ClipboardItem 생성
            await navigator.clipboard.write([
                new ClipboardItem({
                    'image/png': blobPromise,
                }),
            ]);

            toastStore.success('이미지가 복사되었습니다');
        } catch (error) {
            console.error('Image copy failed:', error);
            toastStore.error('복사에 실패했습니다');
        }
    }

    trackDownload(downloadLocation: string) {
        apiClient.news.admin.trackUnsplashImageDownload.mutate({
            downloadLocation,
        });
    }
}

const UnsplashImageCard = observer(
    ({
        image,
        onClickCopy,
    }: {
        image: UnsplashImage;
        onClickCopy?: (imgElement: HTMLImageElement, imageUrl: string) => void;
    }) => {
        let imgRef: HTMLImageElement | null = null;

        return (
            <div className="relative flex flex-col gap-1">
                <img
                    ref={(el) => {
                        imgRef = el;
                    }}
                    loading="lazy"
                    src={image.urls.regular}
                    className="aspect-square w-full rounded object-cover"
                    crossOrigin="anonymous"
                />
                <div className="truncate text-xs text-gray-500">
                    <a
                        className="link link-hover"
                        href={`${image.user.links.html}?utm_source=haewao&utm_medium=referral`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {image.user.username}
                    </a>
                </div>
                {onClickCopy && (
                    <button
                        className="btn btn-ghost btn-sm btn-circle absolute top-2 right-2 z-10 bg-white/80 hover:bg-white"
                        onClick={() => {
                            if (imgRef) {
                                onClickCopy?.(imgRef, image.urls.regular);
                            }
                        }}
                    >
                        <CopyIcon className="h-4 w-4" />
                    </button>
                )}
            </div>
        );
    },
);

export const UnsplashImageSearch = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    return (
        <div className="flex flex-col gap-4">
            <div className="join w-full">
                <input
                    type="text"
                    placeholder="Search for images"
                    className="input input-bordered join-item flex-1"
                    value={viewStore.searchText}
                    onChange={(e) => {
                        const value = e.target.value;
                        viewStore.setSearchText(value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.nativeEvent.isComposing === false) {
                            e.preventDefault();
                            viewStore.search();
                        }
                    }}
                />
                <button
                    className="btn btn-primary join-item"
                    onClick={() => {
                        viewStore.search();
                    }}
                >
                    Search
                </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
                {viewStore.searchedImages.map((image) => (
                    <UnsplashImageCard
                        key={image.id}
                        image={image}
                        onClickCopy={(imgElement) => {
                            viewStore.trackDownload(image.links.download_location);
                            viewStore.copyImage(imgElement);
                        }}
                    />
                ))}
            </div>
        </div>
    );
});
